// One vocabulary for every machine token the conversation protocol puts in front of a
// user. Stages are agent-run step keys, outcomes are workflow checkpoint names, and slot
// keys are capability-registry identifiers: none of them are display copy, and all three
// were reaching the screen verbatim as `brief.resolve_question.1`, `unsupported-task` and
// `source-material: ask`. See BUG-385 and BUG-388.
//
// Anything unrecognised resolves to undefined so the caller renders nothing. Silence is
// better than an identifier, and an unmapped token is a prompt to add copy here.
//
// M9.12 owns the richer treatment, where a change event also names the artifact it acted
// on and the size of the change. This module only removes the leak.

// Execution stages, which reach the status line beneath the composer.
const executionStageCopy: Record<string, string> = {
  // Intake's own first stage. It reaches the progress indicator, never a card, and had no
  // copy while the only thing reading a stage was a status line that never saw it.
  intake: 'Working out what you need',
  'conversation.route-intent': 'Working out what you need',
  'conversation.extract-brief': 'Reading your request',
  'draft-section': 'Drafting the section',
  'finalize-deck': 'Finishing the deck',
  'generate-outline': 'Planning the deck',
  'generate-output': 'Writing the response',
  'generate-slides': 'Writing the slides',
  // The new-proposal graph's only step, and the one graph step with no copy until the
  // progress indicator started reading the whole trail rather than the current stage.
  'parse-template': 'Reading the template',
  'regenerate-outline': 'Replanning the deck',
  'regenerate-slide': 'Rewriting the slide',
  'regenerate-slides': 'Rewriting the slides'
}

// Intake and question checkpoints, which reach the change-event cards in the transcript.
// Some checkpoint keys also arrive as active intake stages before an execution exists.
// Keep their completed wording here and their in-progress wording in executionStageCopy.
const outcomeCopy: Record<string, string> = {
  abandoned: 'Request left unfinished',
  'answer-accepted': 'Answer recorded',
  'answer-stale': 'Answer no longer applies',
  'answer-superseded': 'Answer replaced',
  'brief-created': 'Checked what I already have',
  'budget-exhausted': 'Stopped asking, some details are still open',
  cancelled: 'Cancelled',
  'capability-unavailable': 'Not enabled for your organisation',
  'conversation.extract-brief': 'Read your request',
  'conversation.route-intent': 'Worked out what you need',
  failed: 'Stopped',
  queued: 'Queued',
  questions: 'Asked a question',
  routed: 'Started work',
  running: 'Working',
  succeeded: 'Completed',
  'unsupported-task': 'This action isn’t supported here'
}

// Question steps are numbered per task, arriving as `brief.resolve_question.2` rather
// than a fixed token.
const questionStagePrefixes = ['brief.resolve_question', 'brief.questions']

/** Copy for the status line beneath the composer. */
export function stageLabel(stage: string | undefined): string {
  const trimmed = stage?.trim() ?? ''
  if (trimmed === '') return ''
  const mapped = executionStageCopy[trimmed] ?? outcomeCopy[trimmed]
  if (mapped !== undefined) return mapped
  if (questionStagePrefixes.some((prefix) => trimmed.startsWith(prefix))) return 'Preparing a question'
  return ''
}

/** Copy for a change-event card built from a frame outcome, status or stage. */
export function outcomeLabel(token: string | undefined): string | undefined {
  const trimmed = token?.trim() ?? ''
  if (trimmed === '') return undefined
  return outcomeCopy[trimmed] ?? executionStageCopy[trimmed]
}

/**
 * Turns a capability slot key into prose: `source-material` reads as `source material`.
 * Mechanical rather than a hand-maintained table, so a new registry slot cannot silently
 * fall back to its identifier.
 */
export function slotPhrase(slotKey: string): string {
  return slotKey.trim().replace(/[-_]+/g, ' ')
}

/**
 * Copy for a brief-delta card naming which slots a correction changed. Capped at two
 * named slots plus a count: a correction can touch every slot in the registry, and a
 * card that lists all of them stops being a card. Every name goes through slotPhrase,
 * so a registry identifier can never reach a reader.
 *
 * A correction that names no slots yields undefined and no card, matching this module's
 * rule everywhere else: what it cannot describe, it stays silent about. The old fallback
 * was a bare `Brief updated`, which said nothing the reader did not already know, having
 * just made the correction themselves.
 */
export function briefChangeLabel(changedSlotKeys: ReadonlyArray<string> | undefined): string | undefined {
  const phrases = (changedSlotKeys ?? []).map(slotPhrase).filter((phrase) => phrase !== '')
  if (phrases.length === 0) return undefined
  if (phrases.length === 1) return `Updated ${phrases[0]}`
  if (phrases.length === 2) return `Updated ${phrases[0]} and ${phrases[1]}`
  return `Updated ${phrases[0]}, ${phrases[1]} and ${phrases.length - 2} more`
}

/** Copy for a change-event card describing a question transition. */
export function questionEventLabel(slotKey: string, transition: string | undefined): string | undefined {
  // budget-exhausted spans every remaining slot rather than resolving one, so it arrives
  // with an empty slot key and is described on its own terms.
  if (transition === 'budget-exhausted') return outcomeCopy['budget-exhausted']
  // Abandoning leaves the whole request unfinished rather than dropping one slot, and a
  // run cancelled before it asked anything carries no slot key either.
  if (transition === 'abandoned') return outcomeCopy['abandoned']
  const phrase = slotPhrase(slotKey)
  if (phrase === '') return undefined
  if (transition === undefined) return `Asked about ${phrase}`
  if (transition === 'ask') return `Asked about ${phrase}`
  if (transition === 'accepted') return `Recorded ${phrase}`
  if (transition === 'superseded') return `Replaced ${phrase}`
  return undefined
}

// The producer's slide-version turn, read back into its parts. It arrives as
// `Created slide version: <title>\n\nKey message: <message>` from
// slideVersionMessageContent in backend/internal/domain/proposals/presentation_chat.go,
// and printing that verbatim spends three lines and a boilerplate prefix on what is one
// line of news: this slide now reads like this.
//
// Reading a producer's prose in the frontend is the thing this module exists to stop, so
// it is done here rather than in a component, and it degrades the only safe way: anything
// unrecognised returns undefined and the caller prints the prose it was given. A producer
// that rewords its turn loses the line treatment, never the content.
const slideVersionPrefix = 'Created slide version: '
// Split on the blank line rather than on a colon. Slide titles carry their own colons
// ("Elapsed Timer: Step Boundary Verified"), so the first colon is not the separator.
const slideKeyMessagePrefix = '\n\nKey message: '

export function slideVersionTurn(content: string): { title: string; keyMessage: string } | undefined {
  if (!content.startsWith(slideVersionPrefix)) return undefined
  const rest = content.slice(slideVersionPrefix.length)
  const split = rest.indexOf(slideKeyMessagePrefix)
  if (split < 0) return undefined
  const title = rest.slice(0, split).trim()
  const keyMessage = rest.slice(split + slideKeyMessagePrefix.length).trim()
  if (title === '' || keyMessage === '') return undefined
  return { title, keyMessage }
}

/**
 * How much of an artifact moved, from two absolute counts rather than a signed delta on the
 * wire: a delta cannot tell a created artifact from one whose length did not move, because
 * both arrive as the same number.
 *
 * `No change in length` is the reading that earns this its place on a slide turn. Two
 * refines of one slide can produce two versions whose title and key message are identical,
 * which is what made a chat of refines look like the same turn printed twice, and the length
 * is the only thing on hand that says whether the second one did anything.
 */
export function changeSizeLabel(words: number | undefined, previousWords: number | undefined): string | undefined {
  if (words === undefined) return undefined
  if (previousWords === undefined) return `${words} ${words === 1 ? 'word' : 'words'}`
  const delta = words - previousWords
  if (delta === 0) return 'No change in length'
  return delta > 0 ? `+${delta}` : `${delta}`
}
